/**
 * @fileOverview This file contains the implementation of the Agenda module
 * @version 1.0.0
 * @since 1.0.0
 * @author Miguel Ángel Bermea Rodríguez
 */

//#region Imports
import { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Typography,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import moment from "moment";
import { GlobalVariables } from "../globalVar";
import customAxios from "../utils/customAxios";
import AgendaToolbar from "../components/Agenda/AgendaToolbar";
import AgendaNoRowsOverlay from "../components/Agenda/AgendaNoRowsOverlay";
import "../scss/Pages/_agenda.scss";
import AgendaFooter from "../components/Agenda/AgendaFooter";
import { useTranslationHook } from "../hooks/UseTranslationHook";
import { useNavigate } from "react-router-dom";
//#endregion

const Agenda = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [techsData, setTechsData] = useState([]);
  const [weekStart, setWeekStart] = useState(moment().startOf("week"));
  const [weekEnd, setWeekEnd] = useState(moment().endOf("week"));

  const [techsDataVersion, setTechsDataVersion] = useState(0);

  const navigate = useNavigate();

  const {
    agendaTechHeader,
    agendaStatusCanceled,
    agendaStatusPending,
    agendaStatusOngoing,
    agendaStatusFinished,
    agendaStatusRejected,
    agendaStatusScheduled,
    agendaDaySun,
    agendaDayMon,
    agendaDayTue,
    agendaDayWed,
    agendaDayThu,
    agendaDayFri,
    agendaDaySat,
    agendaVisitType,
    agendaVisitSite,
    agendaVisitDuration,
    agendaVisitStatus,
  } = useTranslationHook();

  /**
   * Returns the color and name associated with a given status.
   *
   * @param {number} status - The status code.
   * @returns {Object} An object containing the color and name associated with the status.
   * @returns {string} Object.color - The color associated with the status.
   * @returns {string} Object.name - The name associated with the status.
   *
   * @example
   * // returns { color: "#A3E0E6", name: "Ongoing" }
   * getStatusColor(3);
   */
  const getStatusColor = (status) => {
    switch (status) {
      default:
        return { color: "#FFFFFF", name: "Desconocido" };
      case 0:
        return { color: "#FF9999", name: agendaStatusCanceled };
      case 1:
        return { color: "#9EFF9E", name: "Creada" }; // Creada NO EXISTE EN ESTA VISTA
      case 2:
        return { color: "#FFFFB3", name: agendaStatusPending }; // Asignada = Pendiente
      case 3:
        return { color: "#A3E0E6", name: agendaStatusOngoing };
      case 4:
        return { color: "#B0C4DE", name: agendaStatusFinished };
      case 5:
        return { color: "#CCCCCC", name: agendaStatusRejected };
      case 6:
        return { color: "#FFD700", name: agendaStatusScheduled };
    }
  };

  /**
   * Generates an array of column objects for Agenda module datagrid.
   *
   * @returns {Object[]} An array of column objects
   */
  const generateColumns = () => {
    const days = [
      { field: "sun", header: agendaDaySun },
      { field: "mon", header: agendaDayMon },
      { field: "tue", header: agendaDayTue },
      { field: "wed", header: agendaDayWed },
      { field: "thu", header: agendaDayThu },
      { field: "fri", header: agendaDayFri },
      { field: "sat", header: agendaDaySat },
    ];

    const columns = [
      {
        field: "tech",
        headerName: agendaTechHeader,
        headerAlign: "center",
        width: 200,
      },
      ...days.map((day, index) => ({
        field: day.field,
        headerName: `${day.header} ${weekStart
          .clone()
          .add(index, "days")
          .format("DD/MM")}`,
        width: 200,
        sortable: false,
        headerAlign: "center",
        renderCell: (params) => (
          <>
            {params.value.map((visit) => (
              <Box key={visit.visitId} onClick={() => navigate(`/visits/details/${visit.visitId}`)}>
                <Tooltip
                  title={
                    <Box p={1}>
                      <Typography variant="body1">
                        {agendaVisitType} {visit.visitType}
                      </Typography>
                      <Typography variant="body1">
                        {agendaVisitSite} {visit.visitSite}
                      </Typography>
                      <Typography variant="body1">
                        {agendaVisitDuration} {visit.visitDuration} h(s)
                      </Typography>
                      <Typography variant="body1">
                        {agendaVisitStatus} {visit.visitStatusName}
                      </Typography>
                    </Box>
                  }
                >
                  <Chip
                    label={visit.visitSite}
                    sx={{
                      backgroundColor: getStatusColor(visit.visitStatus).color,
                      cursor: "pointer",
                      fontWeight: "bold",
                      boxShadow:
                        "rgba(0, 0, 0, 0.07) 0px 1px 2px, rgba(0, 0, 0, 0.07) 0px 2px 4px, rgba(0, 0, 0, 0.07) 0px 4px 8px, rgba(0, 0, 0, 0.07) 0px 8px 16px, rgba(0, 0, 0, 0.07) 0px 16px 32px, rgba(0, 0, 0, 0.07) 0px 32px 64px",
                    }}
                  />
                </Tooltip>
              </Box>
            ))}
          </>
        ),
      })),
    ];
    return columns;
  };

  const columns = generateColumns();

  /**
   * Fetches the weekly visits of technicians from the server and updates the state.
   *
   * This function is called when the component mounts and whenever `weekStart` or `weekEnd` changes.
   */
  useEffect(() => {
    setIsLoading(true);

    customAxios
      .get(`${GlobalVariables.getURL()}getTechsWeekVisits`, {
        headers: {
          'Authorization': 'bearer ' + localStorage.getItem('token')
        }
      })
      .then((response) => {
        const transformedData = response.data.data.map((tech) => {
          const visits = {};

          tech.visits.forEach((visit) => {
            const date = moment(visit.visitDate).format("YYYY-MM-DD");

            if (!visits[date]) {
              visits[date] = [];
            }

            visits[date].push({
              visitId: visit.visitId,
              visitType: visit.visitType,
              visitDuration: visit.visitDuration,
              visitSite: visit.visitSite,
              visitStatusName: visit.statusName,
              visitStatus: visit.status,
            });
          });

          return {
            techId: tech.techId,
            name: `${tech.name} ${tech.last_name}`,
            mobile: tech.mobile,
            visits: visits,
          };
        });
        setTechsDataVersion((prevVersion) => prevVersion + 1);
        setTechsData(transformedData);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fecthing data: ", error);
        setIsLoading(false);
      });
  }, [weekStart, weekEnd]);

  /**
   * Transforms the `techsData` array into a new array of objects suitable for Agenda module datagrid.
   * 
   * @type {Object[]}
   */
  const rows = techsData.map((tech) => {
    let startDate = weekStart.clone(); // Clonar la fecha de inicio
    return {
      id: tech.techId,
      tech: tech.name,
      sun: tech.visits[startDate.format("YYYY-MM-DD")] || [],
      mon: tech.visits[startDate.add(1, "days").format("YYYY-MM-DD")] || [],
      tue: tech.visits[startDate.add(1, "days").format("YYYY-MM-DD")] || [],
      wed: tech.visits[startDate.add(1, "days").format("YYYY-MM-DD")] || [],
      thu: tech.visits[startDate.add(1, "days").format("YYYY-MM-DD")] || [],
      fri: tech.visits[startDate.add(1, "days").format("YYYY-MM-DD")] || [],
      sat: tech.visits[startDate.add(1, "days").format("YYYY-MM-DD")] || [],
    };
  });

  return (
    <Box height={"100%"} width={"100%"} p={"20px"}>
      <DataGridPro
        className="agenda-datagrid"
        disableColumnReorder
        disableColumnFilter
        disableColumnMenu
        disableRowSelectionOnClick
        pinnedColumns={{ left: ["tech"] }}
        loading={isLoading}
        rows={rows}
        columns={columns}
        slots={{
          toolbar: () => (
            <AgendaToolbar
              weekStart={weekStart}
              weekEnd={weekEnd}
              setWeekStart={setWeekStart}
              setWeekEnd={setWeekEnd}
            />
          ),
          footer: () => <AgendaFooter getStatusColor={getStatusColor} />,
          loadingOverlay: LinearProgress,
          noRowsOverlay: AgendaNoRowsOverlay,
        }}
        key={techsDataVersion}
        getRowHeight={() => "auto"}
      />
    </Box>
  );
};

export default Agenda;
